/**
 * Persist per-``(workspace, cwd)`` agent panel preferences so reopening
 * a project comes up where the user left off — same conversation thread
 * and reasoning level — even across a browser reload or a fresh tab.
 *
 * Storage is scoped by ``(workspaceId, cwd)``. The ``cwd`` alone is NOT
 * a unique project identity: every workspace is a separate pod with its
 * own filesystem and its own agent server, and two workspaces almost
 * always share a ``cwd`` path (the same home dir, the same
 * ``/root/<project>``). Keying on ``cwd`` alone would let one
 * workspace's session pointer / preferences bleed into another that
 * happens to sit at the same path. The workspace id is folded into
 * every map key (``"<workspaceId>\u0000<cwd>"``) to keep them isolated.
 *
 * Multiple projects each keep their own pointer + preferences:
 *
 * * Session id — the Workspace Server owns the JSONL transcript at
 *   ``~/.claude/projects/<encoded-cwd>/<session_id>.jsonl``; we just
 *   persist the pointer here.
 * * Effort override — the user's last-picked reasoning level ("low" |
 *   "medium" | "high" | "xhigh" | "max"). Sent as ``?effort=`` on
 *   reconnect so a refreshed tab boots the SDK at the chosen level
 *   instead of snapping back to the baseline.
 * * Permission mode — Execute vs Plan mode. Sent as
 *   ``?permission_mode=`` on connect and adjustable in-session.
 * * Speed mode — Normal vs Fast. Stored per session only so fast
 *   mode never becomes the default for every future session in a
 *   project.
 *
 * On top of the per-cwd map there's a single global pointer:
 *
 * * Active cwd — the project the user was *most recently* inside. A
 *   refresh rehydrates this so the chat re-opens to the same
 *   conversation instead of dropping back to the project picker. We
 *   clear it the moment the user explicitly exits a session (Switch
 *   project / Switch session) so the next refresh respects that
 *   intent.
 */

import type { AgentSpeed, PermissionMode } from './types';

const SESSION_KEY = 'antioch.agent.sessionByCwd.v1';
const EFFORT_KEY = 'antioch.agent.effortByCwd.v1';
const ACTIVE_CWD_KEY = 'antioch.agent.activeCwd.v1';
const PERMISSION_MODES: ReadonlySet<PermissionMode> = new Set([
  'default',
  'acceptEdits',
  'plan',
  'bypassPermissions',
  'dontAsk',
  'auto',
]);
const SPEED_MODES: ReadonlySet<AgentSpeed> = new Set(['normal', 'fast']);

/**
 * Per-(cwd, sessionId) preferences — currently effort, plus room for
 * any future session-specific knob that shouldn't bleed across to
 * other sessions in the same project.
 *
 * Stored as a single localStorage entry to keep migrations simple:
 * one JSON blob keyed by ``"<cwd>|<sessionId>"``. Old per-cwd
 * effort still exists as a fallback for fresh sessions that have
 * never had explicit preferences set.
 */
const SESSION_PREFS_KEY = 'antioch.agent.sessionPrefs.v1';

export interface SessionPrefs {
  /** Reasoning effort — "low" | "medium" | "high" | "xhigh" | "max". */
  effort?: string;
  /** Claude Code permission mode — usually "plan" or "bypassPermissions". */
  permissionMode?: PermissionMode;
  /** Claude Code speed mode — "normal" or "fast". */
  speed?: AgentSpeed;
}

function loadMap(key: string): Record<string, string> {
  if (typeof localStorage === 'undefined') return {};
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    return isStringMap(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

function isStringMap(value: unknown): value is Record<string, string> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  return Object.values(value).every((entry) => typeof entry === 'string');
}

function saveMap(key: string, map: Record<string, string>): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(key, JSON.stringify(map));
  } catch {
    // ignore
  }
}

function upsert(key: string, mapKey: string, value: string | null): void {
  const map = loadMap(key);
  if (value) {
    map[mapKey] = value;
  } else {
    delete map[mapKey];
  }
  saveMap(key, map);
}

/**
 * Compose a workspace-scoped storage key from a workspace id and any
 * further identifying parts (cwd, session id, ...).
 *
 * The NUL (`\u0000`) separator can't appear in a workspace id, a
 * filesystem path, or a session id, so it unambiguously delimits the
 * parts and keeps two workspaces that share a ``cwd`` path from
 * colliding. A ``null`` workspace buckets under ``__none__`` — UI-side
 * keys (runtime cache, drafts, seen-maps) use this for local targets
 * with no workspace id; the persistence writers in this module instead
 * refuse ``null`` outright because a pointer that cannot be attributed
 * to a workspace could never be read back safely.
 *
 * This is THE key format for every workspace-scoped store in the agent
 * panel (this module's maps, the runtime cache, attachment/draft
 * stores, sidebar seen-maps). Build keys through it rather than
 * inlining the format so the scoping can never drift per-store.
 */
export function workspaceScopedKey(workspaceId: string | null, ...parts: string[]): string {
  return [workspaceId ?? '__none__', ...parts].join('\u0000');
}

/**
 * Workspace-scoped map key for a ``cwd`` — thin wrapper over
 * :func:`workspaceScopedKey` for writers that already null-checked
 * the workspace id.
 */
function _scoped(workspaceId: string, cwd: string): string {
  return workspaceScopedKey(workspaceId, cwd);
}

export function loadSessionId(workspaceId: string | null, cwd: string): string | null {
  if (!workspaceId) return null;
  return loadMap(SESSION_KEY)[_scoped(workspaceId, cwd)] ?? null;
}

export function saveSessionId(workspaceId: string | null, cwd: string, sessionId: string | null): void {
  if (!workspaceId) return;
  upsert(SESSION_KEY, _scoped(workspaceId, cwd), sessionId);
}

/** Load the user's last-picked effort for ``(workspaceId, cwd)``, or null if unset. */
export function loadEffort(workspaceId: string | null, cwd: string): string | null {
  if (!workspaceId) return null;
  return loadMap(EFFORT_KEY)[_scoped(workspaceId, cwd)] ?? null;
}

/** Save (or clear, with ``effort=null``) the per-(workspace, cwd) effort override. */
export function saveEffort(workspaceId: string | null, cwd: string, effort: string | null): void {
  if (!workspaceId) return;
  upsert(EFFORT_KEY, _scoped(workspaceId, cwd), effort);
}

/**
 * Read the per-workspace active-cwd map from ``ACTIVE_CWD_KEY``.
 *
 * Accepts three on-disk shapes for forward and backward compatibility:
 *
 *   1. Map shape (current): ``{ [workspaceId]: cwd }`` — every workspace
 *      remembers its own last cwd, switching back restores it.
 *   2. Single-entry shape (prior): ``{ "workspaceId": <id>, "cwd": <c> }``
 *      — migrated into a one-entry map.
 *   3. Bare string (legacy): the original unscoped storage. Dropped
 *      because we cannot attribute it to a workspace.
 */
function _loadCwdMap(): Record<string, string> {
  if (typeof localStorage === 'undefined') return {};
  try {
    const raw = localStorage.getItem(ACTIVE_CWD_KEY);
    if (!raw?.startsWith('{')) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const single = parsed as { workspaceId?: unknown; cwd?: unknown };
    if (typeof single.workspaceId === 'string' && typeof single.cwd === 'string') {
      return { [single.workspaceId]: single.cwd };
    }
    const map: Record<string, string> = {};
    for (const [k, v] of Object.entries(parsed)) {
      if (typeof k === 'string' && k.length > 0 && typeof v === 'string') {
        map[k] = v;
      }
    }
    return map;
  } catch {
    return {};
  }
}

/**
 * Load the cwd of the project the user was last inside on
 * ``workspaceId``. ``null`` when unset, when no workspace is
 * connected, or when no entry has been recorded for this workspace.
 *
 * Scoping by workspace id keeps a remembered local path (e.g.
 * ``/home/<user>/project`` from a previous local-dev workspace) from
 * leaking into a remote staging workspace where that path does not
 * exist, and lets switching between two workspaces restore each one's
 * own most-recent cwd.
 */
export function loadActiveCwd(workspaceId: string | null): string | null {
  if (!workspaceId) return null;
  return _loadCwdMap()[workspaceId] ?? null;
}

/**
 * Persist the active cwd for ``workspaceId``. Pass ``cwd=null`` to
 * clear just this workspace's entry — used by the picker openers so a
 * refresh after "Switch project" / "Switch session" lands on the
 * picker instead of jumping back into the abandoned session. Other
 * workspaces' entries are preserved.
 *
 * Pass ``workspaceId=null`` to clear the entire map outright (no
 * workspace is currently connected).
 */
export function saveActiveCwd(workspaceId: string | null, cwd: string | null): void {
  if (typeof localStorage === 'undefined') return;
  try {
    if (!workspaceId) {
      localStorage.removeItem(ACTIVE_CWD_KEY);
      return;
    }
    const map = _loadCwdMap();
    if (cwd) {
      map[workspaceId] = cwd;
    } else {
      delete map[workspaceId];
    }
    if (Object.keys(map).length === 0) {
      localStorage.removeItem(ACTIVE_CWD_KEY);
    } else {
      localStorage.setItem(ACTIVE_CWD_KEY, JSON.stringify(map));
    }
  } catch {
    // ignore
  }
}

// ---------------------------------------------------------------------------
// Per-(cwd, sessionId) settings — effort, etc.
// ---------------------------------------------------------------------------

function _prefsKey(workspaceId: string, cwd: string, sessionId: string): string {
  return `${workspaceId}\u0000${cwd}|${sessionId}`;
}

function _loadAllPrefs(): Record<string, SessionPrefs> {
  if (typeof localStorage === 'undefined') return {};
  try {
    const raw = localStorage.getItem(SESSION_PREFS_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const normalized: Record<string, SessionPrefs> = {};
    for (const [key, value] of Object.entries(parsed)) {
      if (!value || typeof value !== 'object' || Array.isArray(value)) continue;
      const effort = (value as { effort?: unknown }).effort;
      const permissionMode = (value as { permissionMode?: unknown }).permissionMode;
      const speed = (value as { speed?: unknown }).speed;
      const next: SessionPrefs = {};
      if (typeof effort === 'string' && effort.length > 0) {
        next.effort = effort;
      }
      if (isPermissionMode(permissionMode)) {
        next.permissionMode = permissionMode;
      }
      if (isAgentSpeed(speed)) {
        next.speed = speed;
      }
      if (Object.keys(next).length > 0) {
        normalized[key] = next;
      }
    }
    return normalized;
  } catch {
    return {};
  }
}

function _saveAllPrefs(map: Record<string, SessionPrefs>): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(SESSION_PREFS_KEY, JSON.stringify(map));
  } catch {
    // quota / disabled storage — fall through silently
  }
}

/**
 * Load preferences for ``(cwd, sessionId)``. Returns ``null`` when
 * the user hasn't explicitly set anything for this session yet —
 * caller should fall back to the per-cwd defaults
 * (:func:`loadEffort`).
 */
export function loadSessionPrefs(workspaceId: string | null, cwd: string, sessionId: string): SessionPrefs | null {
  if (!workspaceId) return null;
  const key = _prefsKey(workspaceId, cwd, sessionId);
  const all = _loadAllPrefs();
  const prefs = all[key];
  return prefs ?? null;
}

/**
 * Merge ``patch`` into the saved preferences for ``(cwd, sessionId)``.
 * Pass an undefined value for a field to leave it unchanged; pass
 * ``null`` to clear it (currently coerced to undefined since the
 * stored type is ``string | undefined`` only).
 */
export function patchSessionPrefs(
  workspaceId: string | null,
  cwd: string,
  sessionId: string,
  patch: { effort?: string | null; permissionMode?: PermissionMode | null; speed?: AgentSpeed | null }
): void {
  if (!workspaceId) return;
  const key = _prefsKey(workspaceId, cwd, sessionId);
  const all = _loadAllPrefs();
  const prior = all[key] ?? {};
  const next: SessionPrefs = { ...prior };
  for (const [k, v] of Object.entries(patch) as [keyof SessionPrefs, string | null | undefined][]) {
    if (v === undefined) continue;
    if (v === null) {
      delete next[k];
    } else if (k === 'effort' && typeof v === 'string') {
      next.effort = v;
    } else if (k === 'permissionMode' && isPermissionMode(v)) {
      next.permissionMode = v;
    } else if (k === 'speed' && isAgentSpeed(v)) {
      next.speed = v;
    } else {
      delete next[k];
    }
  }
  // Drop the entry entirely when it's empty so we don't accumulate
  // dead keys forever — most sessions will go un-customized.
  if (Object.keys(next).length === 0) {
    if (key in all) {
      delete all[key];
      _saveAllPrefs(all);
    }
    return;
  }
  all[key] = next;
  _saveAllPrefs(all);
}

/**
 * Drop every saved preference for ``(cwd, sessionId)``. Used when
 * the user deletes a session from the sidebar.
 */
export function clearSessionPrefs(workspaceId: string | null, cwd: string, sessionId: string): void {
  if (!workspaceId) return;
  const key = _prefsKey(workspaceId, cwd, sessionId);
  const all = _loadAllPrefs();
  if (key in all) {
    delete all[key];
    _saveAllPrefs(all);
  }
}

/**
 * One-time cleanup of storage written before workspace scoping.
 *
 * The NUL-scoped key format can never match entries written by older
 * builds (bare ``cwd`` map keys, ``cwd|sessionId`` pref keys,
 * unscoped draft / seen-map localStorage keys), so those entries are
 * permanently unreadable — dead weight that would otherwise sit in
 * localStorage forever. They are dropped rather than migrated because
 * they cannot be attributed to a workspace (the same reasoning as the
 * bare-string active-cwd shape in :func:`_loadCwdMap`). The retired
 * per-(workspace, cwd) permission-mode map is removed outright —
 * permission mode now persists per-session via ``sessionPrefs``.
 *
 * Cheap and idempotent; the provider calls it once per mount.
 */
export function sweepLegacyUnscopedEntries(): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.removeItem('antioch.agent.permissionModeByCwd.v1');
    for (const storageKey of [SESSION_KEY, EFFORT_KEY, SESSION_PREFS_KEY]) {
      const raw = localStorage.getItem(storageKey);
      if (!raw) continue;
      const parsed: unknown = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) continue;
      const keys = Object.keys(parsed);
      const scoped = keys.filter((k) => k.includes('\u0000'));
      if (scoped.length === keys.length) continue;
      if (scoped.length === 0) {
        localStorage.removeItem(storageKey);
      } else {
        const next: Record<string, unknown> = {};
        for (const k of scoped) next[k] = (parsed as Record<string, unknown>)[k];
        localStorage.setItem(storageKey, JSON.stringify(next));
      }
    }
    // Unscoped per-entry keys from the composer draft and sidebar
    // seen-map stores (prefix literals owned by AgentInput.tsx /
    // SessionSidebar.tsx — inlined here so the persistence lib doesn't
    // import UI modules).
    const stale: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k) continue;
      for (const prefix of ['antioch:agent-draft:', 'antioch:session-seen:']) {
        if (k.startsWith(prefix) && !k.slice(prefix.length).includes('\u0000')) stale.push(k);
      }
    }
    for (const k of stale) localStorage.removeItem(k);
  } catch {
    // disabled / quota-restricted storage: nothing to clean
  }
}

function isPermissionMode(value: unknown): value is PermissionMode {
  return typeof value === 'string' && PERMISSION_MODES.has(value as PermissionMode);
}

function isAgentSpeed(value: unknown): value is AgentSpeed {
  return typeof value === 'string' && SPEED_MODES.has(value as AgentSpeed);
}
